import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, Chip, Paper, TextField, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Button, Stack, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Select, InputAdornment, FormControl, InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon, Event as EventIcon,
} from '@mui/icons-material';
import axios from '../api';
import PaginationControls from '../components/PaginationControls';

// Bookings can be pending / approved / rejected / cancelled. Keep the label +
// color mapping in one place so the table, the detail dialog and the filter
// all render every status consistently.
type BookingStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';
const STATUS_META: Record<BookingStatus, { label: string; color: 'warning' | 'success' | 'error' | 'default' }> = {
  pending: { label: 'Pending', color: 'warning' },
  approved: { label: 'Approved', color: 'success' },
  rejected: { label: 'Rejected', color: 'error' },
  cancelled: { label: 'Cancelled', color: 'default' },
};
const statusMeta = (status?: string) =>
  STATUS_META[status as BookingStatus] || { label: status || 'Unknown', color: 'default' as const };

interface BanquetBooking {
  _id: string;
  id?: string;
  banquetHall?: { name?: string; location?: { city?: string } };
  business?: { name?: string };
  customer?: { name?: string; email?: string; phone?: string };
  eventDate: string;
  timeSlot: string;
  guestCount: number;
  eventType?: string;
  contactName?: string;
  contactPhone?: string;
  specialRequests?: string;
  totalAmount: number;
  status: BookingStatus;
  rejectionReason?: string | null;
  createdAt: string;
}

export default function BanquetBookings() {
  const [bookings, setBookings] = useState<BanquetBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedBooking, setSelectedBooking] = useState<BanquetBooking | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [serverStats, setServerStats] = useState<{ total: number; pending: number; approved: number; rejected: number; cancelled: number } | null>(null);

  const fetchBookings = useCallback(async () => {
    try {
      const response = await axios.get('/api/admin/banquet-bookings', {
        params: {
          page,
          limit: rowsPerPage,
          status: statusFilter !== 'All' ? statusFilter : undefined,
        },
      });
      setTotal(response.data.total ?? response.data.bookings?.length ?? 0);
      setServerStats(response.data.stats || null);
      setBookings((response.data.bookings || []).map((booking: any) => ({
        ...booking,
        id: booking._id || booking.id,
      })));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching banquet bookings:', error);
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  // Search only narrows the current page (hall name / business / customer),
  // status filtering and pagination are server-side.
  const filteredBookings = bookings.filter((booking) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      (booking.banquetHall?.name || '').toLowerCase().includes(term) ||
      (booking.business?.name || '').toLowerCase().includes(term) ||
      (booking.customer?.name || '').toLowerCase().includes(term) ||
      (booking.contactName || '').toLowerCase().includes(term)
    );
  });

  const handleOpenBooking = (booking: BanquetBooking) => {
    setSelectedBooking(booking);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // Prefer the backend's platform-wide counts (computed via aggregate) so
  // the cards don't undercount once there's more than one page of bookings.
  const stats = serverStats || {
    total: bookings.length,
    pending: bookings.filter(b => b.status === 'pending').length,
    approved: bookings.filter(b => b.status === 'approved').length,
    rejected: bookings.filter(b => b.status === 'rejected').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length,
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Banquet Bookings</Typography>
        <Typography color="text.secondary">View banquet hall booking requests across the platform.</Typography>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Total Bookings</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>{stats.total}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Pending</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>{stats.pending}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Approved</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>{stats.approved}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Rejected</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>{stats.rejected}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Cancelled</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.secondary' }}>{stats.cancelled}</Typography>
        </Paper>
      </Box>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, p: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search hall, business or customer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 300 } }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              {['All', 'pending', 'approved', 'rejected', 'cancelled'].map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt === 'All' ? 'All' : statusMeta(opt).label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Card>

      {/* Bookings Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.default' }}>
                <TableCell><strong>Hall</strong></TableCell>
                <TableCell><strong>Business</strong></TableCell>
                <TableCell><strong>Customer</strong></TableCell>
                <TableCell><strong>Event Date</strong></TableCell>
                <TableCell><strong>Time Slot</strong></TableCell>
                <TableCell><strong>Guests</strong></TableCell>
                <TableCell><strong>Amount</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <Typography>Loading bookings...</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && filteredBookings.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">No bookings found.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && filteredBookings.length > 0 && (
                filteredBookings.map((booking) => (
                  <TableRow key={booking.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <EventIcon fontSize="small" color="action" />
                        <Typography sx={{ fontWeight: 600 }}>{booking.banquetHall?.name || '—'}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{booking.business?.name || '—'}</TableCell>
                    <TableCell>{booking.customer?.name || booking.contactName || '—'}</TableCell>
                    <TableCell>{new Date(booking.eventDate).toLocaleDateString()}</TableCell>
                    <TableCell>{booking.timeSlot}</TableCell>
                    <TableCell>{booking.guestCount}</TableCell>
                    <TableCell>₹{booking.totalAmount?.toLocaleString?.() ?? booking.totalAmount}</TableCell>
                    <TableCell>
                      <Chip
                        label={statusMeta(booking.status).label}
                        color={statusMeta(booking.status).color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleOpenBooking(booking)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <PaginationControls page={page} total={total} rowsPerPage={rowsPerPage} onPageChange={setPage} onRowsPerPageChange={(value) => { setRowsPerPage(value); setPage(1); }} />

      {/* Booking Detail Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{selectedBooking?.banquetHall?.name || 'Booking Details'}</Typography>
            <Chip
              label={statusMeta(selectedBooking?.status).label}
              color={statusMeta(selectedBooking?.status).color}
              size="small"
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">Business</Typography>
              <Typography sx={{ fontWeight: 600 }}>{selectedBooking?.business?.name || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Customer</Typography>
              <Typography sx={{ fontWeight: 600 }}>{selectedBooking?.customer?.name || selectedBooking?.contactName || '—'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedBooking?.customer?.email || ''} {selectedBooking?.contactPhone ? `· ${selectedBooking.contactPhone}` : ''}
              </Typography>
            </Box>
            <Stack direction="row" spacing={4}>
              <Box>
                <Typography variant="body2" color="text.secondary">Event Date</Typography>
                <Typography sx={{ fontWeight: 600 }}>
                  {selectedBooking?.eventDate ? new Date(selectedBooking.eventDate).toLocaleDateString() : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Time Slot</Typography>
                <Typography sx={{ fontWeight: 600 }}>{selectedBooking?.timeSlot}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Guests</Typography>
                <Typography sx={{ fontWeight: 600 }}>{selectedBooking?.guestCount}</Typography>
              </Box>
            </Stack>
            <Box>
              <Typography variant="body2" color="text.secondary">Event Type</Typography>
              <Typography sx={{ fontWeight: 600 }}>{selectedBooking?.eventType || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Total Amount</Typography>
              <Typography sx={{ fontWeight: 600 }}>
                ₹{selectedBooking?.totalAmount?.toLocaleString?.() ?? selectedBooking?.totalAmount}
              </Typography>
            </Box>
            {selectedBooking?.specialRequests && (
              <Box>
                <Typography variant="body2" color="text.secondary">Special Requests</Typography>
                <Paper sx={{ p: 2, bgcolor: 'action.hover', mt: 1 }}>
                  <Typography variant="body2">{selectedBooking.specialRequests}</Typography>
                </Paper>
              </Box>
            )}
            {selectedBooking?.status === 'rejected' && selectedBooking?.rejectionReason && (
              <Box>
                <Typography variant="body2" color="text.secondary">Rejection Reason</Typography>
                <Typography variant="body2" color="error.main">{selectedBooking.rejectionReason}</Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
